import React, { lazy, Suspense, useEffect, useContext } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { LoadingWrapper } from 'styles/style';
import ReactGA from 'react-ga';
import { useDispatch, useSelector } from 'react-redux';
import commonActions from 'redux/common/actions';
import { PRELOAD_IMAGES } from 'config/settings';
import CustomAppCursor from 'components/CustomAppCursor';
import { MouseContext } from 'context/mouseContext';
import { RootState } from 'redux/rootReducers';

const LandingPage = lazy(() => import('pages/LandingPage'));
const VisualIdentityPage = lazy(() => import('pages/VisualIdentityPage'));
const TncPage = lazy(() => import('pages/TncPage'));

// Initialize google analytics and track every page
ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);
ReactGA.pageview(window.location.pathname + window.location.search);

const NotFound = () => {
    return <Redirect to="/" />;
};

const Routes = (): JSX.Element => {
    const { pathname } = useLocation();

    const dispatch = useDispatch();
    const setTotalAssetsCount = (total: number) => dispatch(commonActions.setTotalAssetsCount(total));
    const addLoadedAssetsCount = () => dispatch(commonActions.addLoadedAssetsCount());

    const totalAssetsCount = useSelector(({ common }: RootState) => common.totalAssetsCount);
    const loadedAssetsCount = useSelector(({ common }: RootState) => common.loadedAssetsCount);

    // Preload images
    useEffect(() => {
        setTotalAssetsCount(PRELOAD_IMAGES.length);

        PRELOAD_IMAGES.forEach((preloadImage) => {
            const image = new Image();
            image.src = preloadImage;
            image.onload = function () {
                addLoadedAssetsCount();
            };
            image.onerror = function () {
                addLoadedAssetsCount();
            };
        });
    }, []);

    // Scroll to top when change path
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    // Handle the mouse cursor UI
    const { cursorChangeHandler } = useContext(MouseContext);

    useEffect(() => {
        if (totalAssetsCount >= loadedAssetsCount && pathname) {
            setTimeout(() => {
                const hoverableElements = document.querySelectorAll('a, button, .hoverable') as NodeListOf<HTMLElement>;

                hoverableElements.forEach((element) => {
                    element.onmouseenter = () => {
                        cursorChangeHandler('hovered');
                    };
                    element.onmouseleave = () => {
                        cursorChangeHandler('');
                    };
                });
            }, 500);
        }
    }, [loadedAssetsCount, pathname]);

    useEffect(() => {
        cursorChangeHandler('');
    }, [pathname]);

    return (
        <Suspense fallback={<LoadingWrapper></LoadingWrapper>}>
            <CustomAppCursor />
            <Switch>
                <Route exact path="/" component={LandingPage} />
                <Route exact path="/visual-identity" component={VisualIdentityPage} />
                <Route exact path="/tnc" component={TncPage} />

                {/* Route Not Found */}
                <Route exact path="*" component={NotFound} />
            </Switch>
        </Suspense>
    );
};

export default Routes;
